import React, { useState, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import styles from './index.module.scss'
import useTitle from '../../../../hooks/useTitle'
import request from '../../../../network/request'
import { formatTimestamp } from '../../../../utils/format'
import Skeleton from '../components/Skeleton'
import { Placeholder } from '../components/Placeholder'
import OptModal from '../components/OptModal'
import ServiceItemEdit from '../components/ServiceItemEdit'
import ReserveKanban from '../components/ReserveKanban'
import Picker from '../../../../components/Picker'
import {
  IRemark,
  IServiceItem,
  IPlanDetail,
  IOrderDetail,
  IReserveCapacity,
} from '../../../../network/models/OrderTypes'
import { getStatusValByKey } from '../common'

import { message } from 'antd'

const SEE_REMARK_NUM = 2

interface ISelectValues {
  dates: string
  times: string
}

interface ISelectOptions {
  dates: string[]
  times: string[]
}

export default function OrderConfirm() {
  const history = useHistory()
  useTitle('订单确认')
  const { orderId } = useParams() as any
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [planInfo, setPlanInfo] = useState<IPlanDetail | any>({})
  const [orderInfo, setOrderInfo] = useState<IOrderDetail | any>({})
  const [estimateTime, setEstimateTime] = useState<number>(1)
  const [showDatetimePicker, setShowDatetimePicker] = useState<boolean>(false)
  const [datetimeValues, setDatetimeValues] = useState<ISelectValues>({
    dates: '',
    times: '',
  })
  const [datetimeOptions, setDatetimeOptions] = useState<ISelectOptions>({
    dates: [],
    times: [],
  })
  const [confirmDatetime, setConfirmDatetime] = useState('')
  const [seeMoreRemark, setSeeMoreRemark] = useState<boolean>(false)
  const [showOptModal, setShowOptModal] = useState(false)
  const [showServiceItemEdit, setShowServiceItemEdit] = useState(false)
  const [opType, setOpType] = useState('')

  useEffect(() => {
    getInitialData()
  }, [])

  const getInitialData = () => {
    setIsLoading(true)
    getPlanDetail()
    getOrderDetail()
  }
  const getPlanDetail = async () => {
    const loaded = message.loading('加载中...', 0)
    try {
      let res: any = await request.get('dragon/service/order/plan/detail', {
        params: {
          orderId,
        },
      })
      setPlanInfo(res)
      setIsError(false)

      if (res.reserveCapacities) {
        const dates = res.reserveCapacities.map((item) =>
          dayjs(item.date).format('YYYY-MM-DD')
        )
        const times = Array.from(new Array(24).keys()).map(
          (item) => `${item}时`
        )

        setDatetimeOptions({ dates, times })
        setDatetimeValues({ dates: dates[0], times: times[0] })
      }
    } catch (err) {
      setIsError(true)
    } finally {
      loaded()
      setIsLoading(false)
    }
  }

  const getOrderDetail = async () => {
    try {
      let res: any = await request.get(`/dragon/service/order/detail`, {
        params: {
          orderId,
        },
      })
      setOrderInfo(res)
    } catch (err) {
      console.log('initial getDetailList err======', err)
    }
  }

  const Reload = () => {
    return (
      <div className={styles.retryWrap} onClick={getInitialData}>
        重试
      </div>
    )
  }

  const handleEstimateTime = (operate: string) => {
    if (operate === 'plus' && estimateTime < 12) {
      setEstimateTime(estimateTime + 0.5)
    } else if (operate === 'minus' && estimateTime >= 1.5) {
      setEstimateTime(estimateTime - 0.5)
    }
  }

  const handleNav = (e) => {
    e.stopPropagation()

    if (window.xdragon) {
      window.xdragon.callHandler('openMap', {
        lat: planInfo?.stationLat,
        lng: planInfo?.stationLot,
        addr: planInfo?.stationAddr,
      })
    }
  }

  const InfoItem = ({
    title,
    children,
  }: {
    title: string
    children: JSX.Element | string
  }) => {
    return (
      <div className={styles.infoItem}>
        <p className={styles.desc}>{title}</p>
        <div className={styles.val}>{children}</div>
      </div>
    )
  }

  const onSelectDateTime = () => {
    setShowDatetimePicker(false)
    setConfirmDatetime(`${datetimeValues.dates} ${datetimeValues.times}`)
  }

  const handleChangeDateTime = (key, value) => {
    setDatetimeValues({ ...datetimeValues, [key]: value })
  }

  const handleShowDatetimePicker = () => {
    setShowDatetimePicker(true)
  }

  const handleClickOpt = (event: React.MouseEvent, op: string) => {
    event.stopPropagation()

    if (op === 'addRemark') {
      setShowOptModal(true)
      setOpType(op)
    }
  }

  const handleCloseOpt = () => {
    setShowOptModal(false)
  }

  const handleRefresh = () => {
    getOrderDetail()
  }

  const handleSubmit = async () => {
    if (!confirmDatetime) {
      message.warning('请选择确认时间')
      return
    }

    const targetDate: any = planInfo?.reserveCapacities?.find(
      (item) =>
        item.date === dayjs(datetimeValues.dates, 'YYYY-MM-DD').valueOf()
    )
    const time = Number(datetimeValues.times.slice(0, -1))

    const timeStamp = dayjs(
      `${datetimeValues.dates} ${time}`,
      'YYYY-MM-DD H'
    ).format('YYYY-MM-DD HH:mm:ss')

    if (!timeStamp) {
      return
    }

    const loaded = message.loading('正在提交...', 0)
    try {
      await request.post('dragon/service/order/reserve/confirm', {
        orderId,
        reserveTimeId: targetDate?.id,
        reserveTime: timeStamp,
        serviceHours: estimateTime,
      })
      history.goBack()
    } catch (err) {
      console.log(err)
    } finally {
      loaded()
    }
  }

  const handleConfirmServiceItemEdit = () => {
    setShowServiceItemEdit(false)
    getOrderDetail()
  }

  if (isLoading) {
    return <Skeleton />
  }

  if (isError) {
    return (
      <Placeholder type="error">
        <Reload></Reload>
      </Placeholder>
    )
  }

  return (
    <div className={styles.contentWrap}>
      <div className={styles.orderInfoItem}>
        <div className={styles.customInfo}>
          <div className={styles.nameStatus}>
            <p className={styles.name}>{planInfo?.stationName}</p>
            <div className={styles.labelContainer}>
              <p className={styles.typeLabel}>远程服务</p>
            </div>
          </div>
          <div className={styles.address} onClick={handleNav}>
            <div className={styles.iconAddress}></div>
            <p className={styles.addressDetail}>{planInfo?.stationAddr}</p>
            <div className={styles.iconArrow}></div>
          </div>
        </div>
      </div>

      <ReserveKanban
        reserveCapacitiesList={planInfo?.reserveCapacities}
        serviceOrderReserveDetails={planInfo?.serviceOrderReserveDetails}
      ></ReserveKanban>

      <div className={styles.contentCardWrap}>
        <div className={styles.orderInfoItem}>
          <div className={styles.seperator}>
            <h3 className={styles.title}>客户信息</h3>
          </div>
          <div className={styles.customerDetail}>
            <div className={styles.content}>
              <p className={styles.name}>{orderInfo?.name}</p>
              <div className={styles.labelContainer}>
                {getStatusValByKey(
                  orderInfo?.status,
                  orderInfo?.orderType,
                  'label'
                ) && (
                  <p
                    className={[
                      styles.statusLabel,
                      styles[
                        getStatusValByKey(
                          orderInfo?.status,
                          orderInfo?.orderType,
                          'labelType'
                        )
                      ],
                    ].join(' ')}
                  >
                    {getStatusValByKey(
                      orderInfo?.status,
                      orderInfo?.orderType,
                      'label'
                    )}
                  </p>
                )}
              </div>
            </div>
            <p className={styles.reserveTime}>
              预约时间：{orderInfo?.reserveTime}
            </p>
          </div>
          <InfoItem title="确认时间">
            <p
              className={styles.orderCodeWrap}
              onClick={handleShowDatetimePicker}
            >
              <span>{confirmDatetime || '请选择'}</span>
              <span className={styles.arrowIcon}></span>
            </p>
          </InfoItem>
          <InfoItem title="预估时长">
            <p className={styles.orderCodeWrap}>
              <span
                className={[
                  styles.minusIcon,
                  estimateTime === 1 ? styles.disabled : '',
                ].join(' ')}
                onClick={() => handleEstimateTime('minus')}
              ></span>
              <span className={styles.estimate}>{estimateTime} 小时</span>
              <span
                className={[
                  styles.plusIcon,
                  estimateTime === 12 ? styles.disabled : '',
                ].join(' ')}
                onClick={() => handleEstimateTime('plus')}
              ></span>
            </p>
          </InfoItem>
          <InfoItem title="报修人">{orderInfo?.reserveName}</InfoItem>
          <InfoItem title="电话">{orderInfo?.mobile}</InfoItem>
          <InfoItem title="车牌">{orderInfo?.plateNo}</InfoItem>
          <InfoItem title="VIN码">{orderInfo?.vin}</InfoItem>
        </div>

        <div className={styles.orderInfoItem}>
          <div className={styles.seperator}>
            <h3 className={styles.title}>服务项目</h3>
          </div>
          <div className={styles.serviceItemsWrap}>
            {orderInfo?.itemDetails?.map((item: IServiceItem) => {
              return (
                <div className={styles.serviceItem}>{item.abbreviation}</div>
              )
            })}
            <div
              className={styles.editServiceItem}
              onClick={() => setShowServiceItemEdit(true)}
            >
              +
            </div>
          </div>
        </div>

        <div className={styles.orderInfoItem}>
          <div className={styles.seperator}>
            <h3 className={styles.title}>备注</h3>
          </div>
          <div className={styles.remarkWrap}>
            {orderInfo?.remarks?.map((remark: IRemark, remarkIndex: number) => {
              return (
                (remarkIndex < SEE_REMARK_NUM || seeMoreRemark) && (
                  <div key={remarkIndex} className={styles.remarkItem}>
                    <div className={styles.markTitle}>
                      <div className={styles.name}>
                        {remark.createName}
                        {remark.createRoleName && (
                          <span className={styles.role}>
                            {remark.createRoleName}
                          </span>
                        )}
                      </div>
                      <div className={styles.time}>
                        {formatTimestamp(remark.createTime)}
                      </div>
                    </div>
                    <p className={styles.remarkContent}>{remark.remark}</p>
                  </div>
                )
              )
            })}
            {orderInfo?.remarks?.length > SEE_REMARK_NUM && (
              <div
                className={styles.seeMoreWrap}
                onClick={() => setSeeMoreRemark(!seeMoreRemark)}
              >
                {seeMoreRemark ? (
                  <>
                    <p className={styles.txt}>收起</p>
                    <div className={styles.upIcon}></div>
                  </>
                ) : (
                  <>
                    <p className={styles.txt}>查看更多</p>
                    <div className={styles.downIcon}></div>
                  </>
                )}
              </div>
            )}
            <div
              className={styles.addRemark}
              onClick={(e) => handleClickOpt(e, 'addRemark')}
            >
              <div className={styles.drawIcon}></div>
              <p className={styles.desc}>输入备注信息</p>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.submitBtnWrap}>
        <div className={styles.submitBtn} onClick={handleSubmit}>
          更新预约
        </div>
      </div>

      {showDatetimePicker && (
        <Picker
          onCancel={() => {
            setShowDatetimePicker(false)
          }}
          onConfirm={onSelectDateTime}
          onChange={handleChangeDateTime}
          valueGroups={datetimeValues}
          optionGroups={datetimeOptions}
        ></Picker>
      )}
      {showOptModal && (
        <OptModal
          opType={opType}
          order={orderInfo}
          onClose={handleCloseOpt}
          onRefreshData={handleRefresh}
        ></OptModal>
      )}
      {showServiceItemEdit && (
        <ServiceItemEdit
          orderId={orderInfo?.id}
          originServiceItems={orderInfo?.itemDetails}
          onConfirm={handleConfirmServiceItemEdit}
          onCancel={() => setShowServiceItemEdit(false)}
        ></ServiceItemEdit>
      )}
    </div>
  )
}
